import ContentLoader from "@funded-here-interface/common/src/components/ContentLoader/ContentLoader";
import { ThanksPageType } from "@funded-here-interface/common/src/constant/enum";
import { getThanksPageUrl } from "@funded-here-interface/common/src/Utils/navigation/constants";
import { FC, ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import useMerchantAccountStatus from "../../hooks/useMerchantAccountStatus";
import { RootState } from "../../store";

interface MerchantAccountGateProps {
  children: ReactNode;
  /**
   * The same `import()` factory the guarded screen's `lazy()` uses. Calling it
   * on mount downloads the screen's chunk alongside the status request instead
   * of after it, and `lazy()` then reads the same cached module.
   */
  preload?: () => Promise<unknown>;
}

/**
 * The auth rep executed the Membership Agreement, so they keep the
 * acknowledgement of that action; the admin gets the neutral setup copy.
 */
const waitingPageFor = (isAuthRep: boolean | null): string =>
  isAuthRep
    ? getThanksPageUrl(ThanksPageType.MERCHANT_AGREEMENT_ACCOUNT_SETUP)
    : getThanksPageUrl(ThanksPageType.COLLECTION_ACCOUNT_PENDING);

/**
 * Blocks the merchant portal until the backend reports onboarding complete and
 * the collection account active. Wrapping the portal routes (rather than only
 * redirecting after login) is what makes a deep link or a refresh honour it.
 *
 * Fails closed: a request error lands on the waiting page, whose poll releases
 * the user once the endpoint answers `isActive: true`. A 401 never gets here —
 * the global interceptor in `reactQuery/index` logs the user out first.
 */
const MerchantAccountGate: FC<MerchantAccountGateProps> = ({ children, preload }) => {
  const isAuthRep = useSelector((state: RootState) => state.auth.isAuthRep);
  const { data, isError } = useMerchantAccountStatus();

  // Immediately, not on idle (cf. AuthSplitLayout): the point is to overlap the
  // status request. A failure here is ignored: lazy() makes its own import(),
  // and a genuine load failure surfaces there (to the root — no error boundary
  // wraps the portal screens). Some browsers cache a failed module fetch, so
  // that second attempt is not guaranteed to be a fresh one.
  useEffect(() => {
    if (preload) void preload().catch(() => {});
  }, [preload]);

  if (data?.isActive === true) {
    return <>{children}</>;
  }

  if (data || isError) {
    return <Navigate to={waitingPageFor(isAuthRep)} replace />;
  }

  return <ContentLoader />;
};

export default MerchantAccountGate;
